import { inject, Injectable } from '@angular/core';
import { HttpUtils } from '@core/utils/http-utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { PricingCountryDialogComponent } from '../components/pricing-country/pricing-country-dialog/pricing-country-dialog.component';
import { EditCurrencyDialogComponent } from '../components/pricing-partner/edit-currency-dialog/edit-currency-dialog.component';
import { EditNotesDialogComponent } from '../components/pricing-partner/edit-notes-dialog/edit-notes-dialog.component';
import { UpdatePricingCountryAction } from '../store/pricing-country.actions';
import {
  PricingCountryFilters,
  PricingCountryStateModel,
} from '../store/pricing-country.state';
import {
  CountryBundlePrice,
  CountryBundlePriceApiModelData,
  CountryBundlePricePivot,
  CountryBundlePricePivotApiModelData,
  PricingCountry,
  PricingCountryApiModelData,
} from '../types/pricing.types';

@Injectable({
  providedIn: 'root',
})
export class PricingCountryUtilsService {
  private ngbModal = inject(NgbModal);
  private store = inject(Store);

  public openPricingCountryDialog(id: string) {
    const modalRef = this.ngbModal.open(PricingCountryDialogComponent, {
      centered: true,
      modalDialogClass: 'modal-size-xxl',
    });

    modalRef.componentInstance.countryId.set(id);
  }

  public openEditCurrencyDialog(id: string) {
    const modalRef = this.ngbModal.open(EditCurrencyDialogComponent, {
      centered: true,
      size: 'modal-size-md',
    });

    modalRef.componentInstance.title.set('pricing.country.editCurrency.title');
    modalRef.componentInstance.placeholder.set(
      'pricing.country.editCurrency.placeholder'
    );
    modalRef.componentInstance.currency.set(
      this.getPricingCountrySnapshot(id)?.currency ?? null
    );
    modalRef.componentInstance.onSave.set((currency: string) =>
      this.store.dispatch(new UpdatePricingCountryAction(id, { currency }))
    );
  }

  public openEditNotesDialog(id: string) {
    const modalRef = this.ngbModal.open(EditNotesDialogComponent, {
      centered: true,
      size: 'modal-size-md',
    });

    modalRef.componentInstance.title.set('pricing.country.editNotes.title');
    modalRef.componentInstance.placeholder.set(
      'pricing.country.editNotes.placeholder'
    );
    modalRef.componentInstance.notes.set(
      this.getPricingCountrySnapshot(id)?.notes ?? null
    );
    modalRef.componentInstance.onSave.set((notes: string) =>
      this.store.dispatch(new UpdatePricingCountryAction(id, { notes }))
    );
  }

  private getPricingCountrySnapshot(id: string): PricingCountry | undefined {
    return this.store.selectSnapshot(
      (state: { pricingCountries: PricingCountryStateModel }) =>
        state.pricingCountries?.pricingCountries?.[id]
    );
  }

  public buildPricingCountryHttpParams(params: PricingCountryFilters) {
    let httpParams = HttpUtils.buildHttpParams();

    if (params.countryIds?.length) {
      httpParams = httpParams.set('country_ids', params.countryIds.join(','));
    }

    if (params.name) {
      httpParams = httpParams.set('name', params.name);
    }

    if (params.currency) {
      httpParams = httpParams.set('currency', params.currency);
    }

    if (params.notes) {
      httpParams = httpParams.set('notes', params.notes);
    }

    if (params.limit) {
      httpParams = httpParams.set('limit', params.limit);
    }

    if (params.page) {
      httpParams = httpParams.set('page', params.page);
    }

    return httpParams;
  }

  public apiModelToPricingCountryBundlePivot(
    model: CountryBundlePricePivotApiModelData
  ): CountryBundlePricePivot {
    const price =
      model.price === null || model.price === undefined
        ? null
        : Number(model.price);

    return {
      id: model.id,
      countryId: model.country_id,
      bundleId: model.bundle_id,
      price: Number.isNaN(price) ? null : price,
      type: model.type,
      validFrom: new Date(model.valid_from),
      generatedBy: model.generated_by ?? null,
    };
  }

  public apiModelToPricingCountryBundle(
    model: CountryBundlePriceApiModelData
  ): CountryBundlePrice | null {
    if (!model?.pivot) {
      return null;
    }

    return {
      id: model.id,
      name: model.name,
      internalName: model.internal_name ?? model.name,
      pivot: this.apiModelToPricingCountryBundlePivot(model.pivot),
    };
  }

  public apiModelToPricingCountry(
    model: PricingCountryApiModelData
  ): PricingCountry {
    const pricingCountry: PricingCountry = {
      id: model.id,
      name: model.name,
      currency: model.currency ?? null,
      notes: model.notes ?? null,
    };

    if (model.pricelist) {
      pricingCountry.pricelist = model.pricelist
        .map((bundlePrice: CountryBundlePriceApiModelData) =>
          this.apiModelToPricingCountryBundle(bundlePrice)
        )
        .filter((bundlePrice) => !!bundlePrice);
    }

    return pricingCountry;
  }
}
